import { Citation } from '#/api'
import { IconButton, Stack } from '@fluentui/react'

import styles from './FilePreview.module.css'
import { PdfPreviewPanel } from './PdfPreview'
import { CitationPanel } from './CitationPanel'
import { GetPreviewType, TryGetFileExtension, GetDefaultCitation, GetDefaultAgent } from './FilePreview.utils'
import { PreviewType } from './FilePreview.types'
import { useCallback, useMemo } from 'react'
import { config } from '#/config'

export type FilePreviewProps = {
  citation?: Citation,
  agent?: string,
  onCloseClick: (citation: Citation) => void
  onTitleClick: (citation: Citation) => void
}

export const FilePreview = ({ citation, agent, onCloseClick, onTitleClick }: FilePreviewProps) => {
  const agentLive = config.env.NEW_ARCHITECT_LIVE;
  const internalCitation = useMemo(() => citation ?? GetDefaultCitation(), [citation])
  const internalAgent = useMemo(() => agent ?? GetDefaultAgent(), [agent])

  const previewType = useMemo(() => {
    const fileExtension = internalCitation.fileExtension ?? TryGetFileExtension(internalCitation.title)
    const previewType = GetPreviewType(fileExtension, internalCitation.url, internalAgent)
    return previewType
  }, [internalCitation, internalAgent])

  const handleOnCloseClick = useCallback(() => {
    onCloseClick(internalCitation)
  }, [internalCitation])

  const handleOnTitleClick = useCallback(() => {
    onTitleClick(internalCitation)
  }, [internalCitation])

  const title = internalCitation.title ?? ''
  const h5title = internalCitation.url && !internalCitation.url.includes('blob.core') ? internalCitation.url : title

  return (
    <Stack.Item className={styles.filePreviewContainer} style={agentLive ? { height: '100vh' } : { height: 'calc(100vh - 100px)' }} tabIndex={0} role="tabpanel" aria-label="Citations Panel">
      <Stack
        aria-label="Citations Panel Header Container"
        horizontal
        className={styles.filePreviewHeaderContainer}
        horizontalAlign="space-between"
        verticalAlign="center">
        <span aria-label="Citations" className={styles.filePreviewHeader}>
          Citations
        </span>
        <IconButton
          iconProps={{ iconName: 'Cancel' }}
          aria-label="Close citations panel"
          onClick={handleOnCloseClick}
        />
      </Stack>
      <h5 className={styles.filePreviewTitle} tabIndex={0} title={h5title} onClick={handleOnTitleClick}>
        {title}
      </h5>
      <div tabIndex={0} className={styles.filePreviewContentContainer}>
        {previewType == PreviewType.PDF && <PdfPreviewPanel citation={internalCitation} />}
        {previewType == PreviewType.DEFAULT && <CitationPanel citation={internalCitation} />}
      </div>
    </Stack.Item>
  )
}
